// TasksCard — Home "Tasks" action queue (design bundle: home.jsx TasksCard).
//
// Shows the flat aggregated task feed (MF_TASKS.useListTasks → /members/tasks,
// the same data the extension serves): access approvals, provisioning /
// deprovisioning, batches, entitlement adjustments, offboarding, and access
// review campaigns — all in one list, not grouped task lists.
//
// Actionable tasks (assignment types with an assigneeId) get inline ✓/✗ icons
// that, when clicked, animate into a Confirm button + an "Add note" button.
// Confirm responds in place (no note); Add note opens the approve/deny modal
// (optional note for approve, required reason for deny). Clicking away reverts
// to the icons. Offboarding / campaign rows navigate to their page. The card
// self-hides when the queue is empty.

import { Box, Flex, HStack, Icon, Text } from "@chakra-ui/react";
import { useState } from "react";
import { TbChevronRight } from "react-icons/tb";

import Card from "components/ds/Card";
import CardHeader from "components/ds/CardHeader";
import TaskActions from "components/tasks/TaskActions";
import TaskDrawer from "components/tasks/TaskDrawer";
import TaskSubjectAvatar from "components/tasks/TaskSubjectAvatar";
import { isActionableTask, taskRowKey, typeMeta } from "components/tasks/taskMeta";
import { MF_TASKS, type MFTaskItem } from "state/members/tasks";

const TypeCell = ({ entityType }: { entityType: string }) => {
  const meta = typeMeta(entityType);
  return (
    <HStack as="span" spacing="sm" w="160px" flexShrink={0}>
      <Box w="12px" h="8px" borderRadius="full" bg={meta.color} flexShrink={0} />
      <Box as="span" fontSize="md" fontWeight={500} noOfLines={1}>
        {meta.label}
      </Box>
    </HStack>
  );
};

const TaskSubject = ({ task }: { task: MFTaskItem }) => (
  <Box flex="1" minW={0}>
    <Text fontSize="md" fontWeight={500} color="neutral.950" noOfLines={1}>
      {task.title}
    </Text>
    {task.body && (
      <Text fontSize="smd" color="neutral.700" noOfLines={1}>
        {task.body}
      </Text>
    )}
  </Box>
);

// design ref: styles.css:938–946 + .card--tasks .inner-table .task-row (8px pad)
const ROW_PROPS = {
  align: "center" as const,
  gap: "lg" as const, // 16px
  p: "sm" as const, // 8px
  borderRadius: "component.lg" as const,
  minH: "48px",
};

// Every row opens the detail drawer; actionable rows also keep their inline
// ✓/✗ (which stop propagation so they don't also open the drawer).
const TaskRow = ({
  task,
  onOpen,
}: {
  task: MFTaskItem;
  onOpen: () => void;
}) => {
  const actionable = isActionableTask(task);

  return (
    <Flex
      {...ROW_PROPS}
      cursor="pointer"
      transition="background-color 120ms"
      _hover={{ bg: "bg.hover" }}
      onClick={onOpen}
    >
      <TypeCell entityType={task.entityType} />
      <TaskSubjectAvatar task={task} />
      <TaskSubject task={task} />
      {actionable ? (
        <Box flexShrink={0} onClick={(e) => e.stopPropagation()}>
          <TaskActions task={task} />
        </Box>
      ) : (
        <>
          {task.taskType === "offboarding" && typeof task.accountCount === "number" ? (
            <Text fontSize="smd" color="neutral.700" whiteSpace="nowrap" flexShrink={0}>
              {task.accountCount} account{task.accountCount === 1 ? "" : "s"}
            </Text>
          ) : null}
          <Icon as={TbChevronRight} boxSize="16px" color="neutral.500" flexShrink={0} />
        </>
      )}
    </Flex>
  );
};

export interface TasksCardProps {
  tasks: MFTaskItem[];
  onViewAll: () => void;
}

const TasksCard = ({ tasks, onViewAll }: TasksCardProps) => {
  const [selected, setSelected] = useState<MFTaskItem | null>(null);

  // No outstanding tasks → the card doesn't show at all.
  if (tasks.length === 0) return null;

  return (
    <Card variant="tasks">
      <CardHeader
        title="Tasks"
        count={tasks.length}
        action={{ label: "View all", onClick: onViewAll }}
      />
      {/* design ref: styles.css:208–228 — .inner-table, fixed-height scroll */}
      <Box
        bg="neutral.25"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="neutral.200"
        borderRadius="component.2xl"
        overflow="hidden"
        p="sm"
        maxH="264px"
        overflowY="auto"
        display="flex"
        flexDirection="column"
        gap="md"
        sx={{
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            background: "var(--chakra-colors-neutral-300)",
            borderRadius: "8px",
          },
        }}
      >
        {tasks.map((task, i) => (
          <TaskRow
            key={taskRowKey(task, i)}
            task={task}
            onOpen={() => setSelected(task)}
          />
        ))}
      </Box>

      <TaskDrawer task={selected} onClose={() => setSelected(null)} />
    </Card>
  );
};

export default TasksCard;
